/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, Suspense, ReactNode } from 'react';
import { Router } from 'react-router-dom';
import { createHashHistory } from 'history';
import { ApolloProvider } from '@apollo/client';
import { SpinnerFullPage } from '@userzoomlibraries/toolkit-spinner';
import { DeviceProvider } from '@userzoomplatform/pe-study-layout';

import CookiesGuard from './hocs/cookies-guard';
import Routes from './Routes';
import ErrorBoundary from './hocs/error-boundary';
import createGraphQlClient from './api/graphql-client';
import GA from './hocs/ga';
import Main from './components/main';
import { WebExtensionProvider } from './providers/web-extension';
import { useToken } from './hooks';
import config from './utils/config';
import withSplit from './hocs/with-split';

const hashHistory = createHashHistory();

type Props = {
	children: ReactNode;
};

const ApolloClient = ({ children }: Props): ReactElement => {
	const token = useToken();
	const getToken = () => token;
	const graphQlClient = createGraphQlClient({ uri: config().apiUrl, getToken });

	return <ApolloProvider client={graphQlClient}>{children}</ApolloProvider>;
};

const App = (): ReactElement => (
	<ErrorBoundary>
		<Router history={hashHistory}>
			<GA>
				<ApolloClient>
					<Suspense fallback={<SpinnerFullPage />}>
						<Main>
							<CookiesGuard>
								<DeviceProvider>
									<WebExtensionProvider>
										<Routes />
									</WebExtensionProvider>
								</DeviceProvider>
							</CookiesGuard>
						</Main>
					</Suspense>
				</ApolloClient>
			</GA>
		</Router>
	</ErrorBoundary>
);

export default withSplit(App);
